import React, { useCallback, useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import FormDate from "../../../components/formFeild/FormDate";
import baseUrl from "../../../api/baseUrl";
import PercentIcon from "@mui/icons-material/Percent";
import PersonIcon from "@mui/icons-material/Person";
import AssessmentIcon from "@mui/icons-material/Assessment";
import CommonLayout from "../../../layouts/commonLayout/CommonLayout";

interface FormData {
  startDate: string;
  endDate: string;
}

interface DentistCommission {
  dentistId: string;
  dentistName: string;
  totalCommission: number;
  treatmentCount: number;
  lineItems: {
    treatment: string;
    isOthers?: boolean;
    price: number;
    quantity: number;
    commission: number;
    paymentDate?: string;
    amountReceived?: number;
  }[];
}

function getDefaultMonthRange(): { startDate: string; endDate: string } {
  const end = new Date();
  const start = new Date();
  start.setMonth(start.getMonth() - 1);
  return {
    startDate: start.toISOString().slice(0, 10),
    endDate: end.toISOString().slice(0, 10),
  };
}

function CommissionReportPage() {
  const defaultRange = getDefaultMonthRange();
  const methods = useForm<FormData>({
    defaultValues: {
      startDate: defaultRange.startDate,
      endDate: defaultRange.endDate,
    },
  });
  const [data, setData] = useState<DentistCommission[]>([]);
  const [loading, setLoading] = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const onSubmit = useCallback(async (formData: FormData) => {
    if (!formData.startDate || !formData.endDate) return;
    setLoading(true);
    try {
      const response = await baseUrl.get("payment/commissionReport", {
        params: {
          startDate: formData.startDate,
          endDate: formData.endDate,
        },
      });
      setData(response.data.data || []);
    } catch (error) {
      console.error("Error fetching commission report:", error);
      setData([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void onSubmit(getDefaultMonthRange());
  }, [onSubmit]);

  return (
    <CommonLayout>
      <div className="mt-6 space-y-4">
        {/* Header Card */}
        <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-lg shadow-slate-200/50 dark:shadow-slate-900/50 border border-slate-100 dark:border-slate-700 p-4">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-xl flex items-center justify-center">
                <PercentIcon className="text-white" sx={{ fontSize: 24 }} />
              </div>
              <div>
                <h3 className="text-lg font-bold text-slate-800 dark:text-slate-200">Commission Report</h3>
                <p className="text-sm text-slate-500 dark:text-slate-400">View dentist commissions by treatment</p>
              </div>
            </div>
          </div>
        </div>

        {/* Filter & Table */}
        <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-lg shadow-slate-200/50 dark:shadow-slate-900/50 border border-slate-100 dark:border-slate-700 overflow-hidden">
          <FormProvider {...methods}>
            <form
              onSubmit={methods.handleSubmit(onSubmit)}
              className="p-4 border-b border-slate-100 dark:border-slate-600"
            >
              <div className="flex flex-nowrap items-end gap-3">
                <FormDate label="From" name="startDate" sameSize />
                <FormDate label="To" name="endDate" sameSize />
                <button
                type="submit"
                disabled={loading}
                className="h-8 px-4 rounded-lg bg-gradient-to-r from-primary to-primaryLight text-white font-medium hover:shadow-lg hover:shadow-primary/20 transition-all text-sm disabled:opacity-50 flex items-center gap-2"
              >
                <AssessmentIcon sx={{ fontSize: 18 }} />
                {loading ? "Loading..." : "Generate Report"}
              </button>
              </div>
            </form>
          </FormProvider>

          <div className="overflow-x-auto">
            {data.length === 0 && !loading ? (
              <div className="flex flex-col items-center justify-center py-16 text-slate-400 dark:text-slate-500">
                <PercentIcon sx={{ fontSize: 56 }} className="mb-3 opacity-50" />
                <p className="text-sm">Select date range and click Generate Report</p>
              </div>
            ) : (
              <table className="w-full">
                <thead>
                  <tr className="bg-slate-50 dark:bg-slate-700/50 border-b border-slate-200 dark:border-slate-600">
                    <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                      Dentist
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                      Treatments
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                      Total Commission
                    </th>
                    <th className="px-6 py-3 w-12" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-600">
                  {data.map((row) => (
                    <React.Fragment key={row.dentistId}>
                      <tr
                        className="hover:bg-slate-50/50 dark:hover:bg-slate-700/50 cursor-pointer transition-colors"
                        onClick={() => setExpandedId(expandedId === row.dentistId ? null : row.dentistId)}
                      >
                        <td className="px-6 py-3">
                          <div className="flex items-center gap-2">
                            <PersonIcon sx={{ fontSize: 18 }} className="text-slate-400 dark:text-slate-500" />
                            <span className="font-medium text-slate-800 dark:text-slate-200">{row.dentistName || "Unknown"}</span>
                          </div>
                        </td>
                        <td className="px-6 py-3 text-right text-slate-600 dark:text-slate-400">{row.treatmentCount}</td>
                        <td className="px-6 py-3 text-right font-semibold text-emerald-600 dark:text-emerald-400">
                          {row.totalCommission.toFixed(2)}
                        </td>
                        <td className="px-6 py-3 text-slate-400 dark:text-slate-500 text-sm">
                          {expandedId === row.dentistId ? "−" : "+"}
                        </td>
                      </tr>
                      {expandedId === row.dentistId && row.lineItems?.length > 0 && (
                        <tr>
                          <td colSpan={4} className="px-6 py-3 bg-slate-50/80 dark:bg-slate-700/50">
                            <div className="text-sm space-y-2 max-h-60 overflow-y-auto pl-8">
                              <p className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase mb-2">Treatment breakdown</p>
                              {row.lineItems.map((item, i) => (
                                <div
                                  key={i}
                                  className="flex justify-between items-center py-1.5 border-b border-slate-100 dark:border-slate-600 last:border-0"
                                >
                                  <span className="text-slate-600 dark:text-slate-400 flex flex-col">
                                    <span>
                                      {item.isOthers ? (
                                        <>Others{item.treatment ? ` – ${item.treatment}` : ""}</>
                                      ) : (
                                        item.treatment
                                      )}
                                    </span>
                                    {(item.paymentDate || item.amountReceived != null) && (
                                      <span className="text-xs text-slate-500 dark:text-slate-400">
                                        {item.paymentDate && (
                                          <>Payment: {String(item.paymentDate).split("T")[0]} </>
                                        )}
                                        {item.amountReceived != null && (
                                          <>• Received: {Number(item.amountReceived).toFixed(2)}</>
                                        )}
                                      </span>
                                    )}
                                  </span>
                                  <span className="flex items-center gap-4">
                                    <span className="text-slate-500 dark:text-slate-400 text-xs">
                                      {item.price != null && item.price !== undefined ? `${Number(item.price).toFixed(2)} × ${item.quantity || 1}` : ""}
                                    </span>
                                    <span className="text-emerald-600 dark:text-emerald-400 font-medium">{item.commission.toFixed(2)}</span>
                                  </span>
                                </div>
                              ))}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    </CommonLayout>
  );
}

export default CommissionReportPage;
